import { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import TextLink from 'LuxKit/TextLink'
import { invalidateTrip } from 'tripPlanner/hooks/api/common'
import type { RegenerateDayAiParams } from 'tripPlanner/api'
import { regenerateDayAi } from 'tripPlanner/api'
import Group from 'components/utils/Group'
import type { TripItem } from 'tripPlanner/types/tripItem'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'

interface SuggestReplanDayBannerProps {
  item: TripItem
  onRegenerateStart: (date: string) => void
  onRegenerateEnd: (date: string) => void
  date: string
  handleDismissBanner: (itemId: string) => void
}

function SuggestReplanDayBanner({ item, onRegenerateStart, onRegenerateEnd, date, handleDismissBanner }: SuggestReplanDayBannerProps) {
  const intl = useIntl()
  const queryClient = useQueryClient()

  const handleDismiss = useCallback(() => {
    handleDismissBanner(item.id)
  }, [handleDismissBanner, item.id])

  const regenerateDayMutation = useMutation({
    mutationFn: ({ tripId, date, userInput, alternativeGroup }: RegenerateDayAiParams) =>
      regenerateDayAi({
        tripId,
        date,
        userInput,
        alternativeGroup,
        itemId: item.id,
      }),
    onSuccess: () => {
      invalidateTrip(queryClient, item.tripId)
      onRegenerateEnd(date)
    },
    onError: () => {
      showSnackbar(intl.formatMessage({
        id: 'E3jdsKKOSwNVyUXrjZmK',
        defaultMessage: 'Failed to regenerate day. Please try again.',
        description: 'Error snackbar shown when regenerating a trip day fails',
      }), 'success', { icon: <SolidCheckCircleIcon /> })
    },
  })

  const handleRegenerateDay = useCallback((userInput?: string) => {
    onRegenerateStart(date)
    handleDismissBanner(item.id)

    const trimmedUserInput = userInput?.trim()

    const payload: RegenerateDayAiParams = {
      tripId: item.tripId,
      date,
    }

    if (trimmedUserInput) {
      payload.userInput = trimmedUserInput
    }

    regenerateDayMutation.mutate(payload)
  }, [onRegenerateStart, date, handleDismissBanner, item.id, item.tripId, regenerateDayMutation])

  const onClick = useCallback(() => {
    handleRegenerateDay()
  }, [handleRegenerateDay])

  return (
    <MessageBanner
      kind="success"
      align="center"
      icon={<LineCheckIcon />}
      secondaryActionButtonProps={{
        children: intl.formatMessage({
          id: 'onYhPBSD77U9w8bIc-_D',
          defaultMessage: 'Dismiss',
          description: 'Button to dismiss the replan suggestion banner component',
        }),
        onClick: handleDismiss,
      }}
    >
      <Group direction="horizontal" gap={12}>
        <FormattedMessage
          id="ztQS5BvD_A9ZoRUECRgw"
          defaultMessage="{itemName} has been replanned. <fixLink>Click here to fix related items</fixLink>"
          description="Message indicating a trip item has been replanned with link to fix related items"
          values={{
            itemName: item.sortableName,
            fixLink: (chunks) => (
              <TextLink onClick={onClick}>{chunks}</TextLink>
            ),
          }}
        />
      </Group>
    </MessageBanner>
  )
}

export default SuggestReplanDayBanner
